/**
 * FP3-043 Phase A follow-up #9 — Admin Oversight banner for the
 * student workspace.
 *
 * Renders at the top of the student workspace any time an admin-
 * family user (admin / org_admin / super_admin) opens the student
 * URL WITHOUT shadowing a specific provider (i.e. they clicked a
 * student from Admin → Students, Progress, Service Fidelity, etc.).
 *
 * Distinct from the existing `ReadOnlyProviderBanner`, which only
 * activates when `?as_provider=<pid>` is present.
 *
 * Visual treatment matches the existing read-only banner family
 * (amber/gold, calm, oversight not error) but uses a different
 * heading + subline so the user knows they are NOT shadowing a
 * specific provider.
 *
 * The banner does NOT enforce read-only at the data layer — the
 * backend gates provider-only mutations on its own. The banner is
 * a calm UX layer that matches the actual permission state.
 */
import { Eye } from "@phosphor-icons/react";
import { useStudentWorkspaceReadOnly } from "./ReadOnlyProviderBanner";

export default function AdminStudentOversightBanner() {
  const readOnly = useStudentWorkspaceReadOnly();
  if (!readOnly.active || readOnly.mode !== "admin_oversight") return null;
  return (
    <div
      data-testid="admin-oversight-banner"
      className="relative bg-gradient-to-r from-[#FDF3D8] via-[#FBEBC0] to-[#F8E2AE] border-y-2 border-[#C8951F] px-5 py-3.5 flex items-center justify-between gap-4 shadow-sm"
    >
      <span
        aria-hidden="true"
        className="absolute left-0 top-0 bottom-0 w-1.5 bg-[#9C6B2E]"
      />
      <div className="flex items-center gap-3 min-w-0 pl-3">
        <span
          aria-hidden="true"
          className="shrink-0 w-9 h-9 rounded-full bg-[#9C6B2E] flex items-center justify-center"
        >
          <Eye size={18} weight="duotone" className="text-white" />
        </span>
        <div className="min-w-0">
          <p
            data-testid="admin-oversight-banner-heading"
            className="text-[14px] font-semibold text-[#5A4A1A] leading-snug heading-font truncate"
          >
            Admin View — Student workspace is read-only
          </p>
          <p
            data-testid="admin-oversight-banner-subline"
            className="text-[12px] text-[#7A6A2C] leading-snug mt-0.5"
          >
            You are viewing this student as an administrator. Provider
            documentation and therapy actions are disabled.
          </p>
        </div>
      </div>
    </div>
  );
}
